import type { CaseBattleDetailsResponse } from '../../services/generated/Api';
import { battleShowMs, roundRevealMs, roundStarts, type RoundShowLike } from './showTiming';

export interface Seat {
  id: string;
  slot: number;
  userId: string;
  firstName?: string | null;
  level?: number;
  startNow: boolean;
  totalDroppedValue: number;
  payout: number;
}

export interface BattleLike {
  id: string;
  status: string;
  mode: string;
  isTeam: boolean;
  visibility: string;
  maxPlayers: number;
  totalCost: number;
  participants: Seat[];
}

export type BattleMode = 'classic' | 'crazy' | 'sharing' | 'last_case';

export const BATTLE_MODES: BattleMode[] = ['classic', 'crazy', 'sharing', 'last_case'];

export const sideOf = (slot: number): 0 | 1 => (slot < 2 ? 0 : 1);

export const isRunning = (status: string): boolean => status === 'PROCESSING' || status === 'IN_PROGRESS';

export interface ShowLike {
  status: string;
  completedAt?: string | null;
  rounds: RoundShowLike[];
  participants: unknown[];
}

export const showEndsAt = (battle: ShowLike): number | null => {
  if (battle.status !== 'COMPLETED' || !battle.completedAt) return null;
  return Date.parse(battle.completedAt) + battleShowMs(battle.rounds, battle.participants.length);
};

export const isShowing = (battle: ShowLike, now: number): boolean =>
  isRunning(battle.status) || (showEndsAt(battle) ?? 0) > now;

export const isLiveInLobby = (battle: ShowLike, now: number): boolean =>
  battle.status === 'WAITING' || isShowing(battle, now);

export const showRoundIndex = (battle: ShowLike, now: number): number => {
  if (!battle.completedAt) return 0;
  const starts = roundStarts(Date.parse(battle.completedAt), battle.participants.length, battle.rounds);
  const index = starts.findLastIndex((start, i) => i < battle.rounds.length && start <= now);
  return Math.max(0, index);
};

export const roundRevealAt = (battle: ShowLike, index: number): number | null => {
  if (!battle.completedAt) return null;
  const starts = roundStarts(Date.parse(battle.completedAt), battle.participants.length, battle.rounds);
  return starts[index] + roundRevealMs(battle.participants.length);
};

export const revealedRounds = (battle: ShowLike, now: number): number => {
  if (battle.status !== 'COMPLETED') return 0;
  if (!battle.completedAt || !isShowing(battle, now)) return battle.rounds.length;
  const starts = roundStarts(Date.parse(battle.completedAt), battle.participants.length, battle.rounds);
  const reveal = roundRevealMs(battle.participants.length);
  return battle.rounds.filter((_, i) => starts[i] + reveal <= now).length;
};

export const modeOf = (mode: string): BattleMode =>
  mode === 'crazy' || mode === 'sharing' || mode === 'last_case' ? mode : 'classic';

export const seatsBySlot = <S extends Pick<Seat, 'slot'>>(maxPlayers: number, participants: S[]): (S | undefined)[] =>
  Array.from({ length: maxPlayers }, (_, slot) => participants.find((p) => p.slot === slot));

export const reasonKey = (reason: string | undefined) => {
  switch (reason) {
    case 'not_waiting': return 'reasonNotWaiting' as const;
    case 'full': return 'reasonFull' as const;
    case 'already_in': return 'reasonAlreadyIn' as const;
    case 'insufficient_balance': return 'reasonInsufficientBalance' as const;
    case 'trade_link_required': return 'reasonTradeLinkRequired' as const;
    case 'max_active_battles': return 'reasonMaxActiveBattles' as const;
    case 'withdrawals_blocked': return 'reasonWithdrawalsBlocked' as const;
    case 'multiacc': return 'reasonMultiacc' as const;
    case 'not_participant': return 'reasonNotParticipant' as const;
    case 'not_completed': return 'reasonNotCompleted' as const;
    case 'nothing_to_sell': return 'reasonNothingToSell' as const;
    case 'rematch_expired': return 'reasonRematchExpired' as const;
    case 'rematch_exists': return 'reasonRematchExists' as const;
    case 'no_opponents': return 'reasonNoOpponents' as const;
    default: return null;
  }
};

export const canDo = (battle: Pick<CaseBattleDetailsResponse, 'actions'>, action: string): boolean =>
  battle.actions.includes(action);
